import { assertLayoutNodeExhaustive } from './layoutAssertNever';
import { walkLayoutStructureChildren } from './layoutStructureChildLists';
import type {
  LayoutInlineStyle,
  LayoutNode,
  TableNode,
  TableRowEntry,
} from './types';

export type LayoutPlainCssHiddenQid = {
  qid: string;
  reason: string;
};

const HIDING_CLASS_TOKENS = new Set(['hidden', 'invisible', 'opacity-0']);

type CssHidingReason = {
  field: string;
  value: string;
};

function normalizeCssValue(value: string | number | undefined): string {
  if (value == null) return '';
  return String(value).trim().toLowerCase();
}

function classTokens(className: string | undefined): string[] {
  if (!className?.trim()) return [];
  return className.trim().split(/\s+/);
}

function formatCssHidingReason(reason: CssHidingReason): string {
  return `${reason.field} "${reason.value}"`;
}

function hidingReasonFromStyle(
  style: LayoutInlineStyle,
  fieldPrefix = 'style'
): CssHidingReason | null {
  const display = normalizeCssValue(style.display);
  if (display === 'none') {
    return { field: `${fieldPrefix}.display`, value: 'none' };
  }

  const visibility = normalizeCssValue(style.visibility);
  if (visibility === 'hidden') {
    return { field: `${fieldPrefix}.visibility`, value: 'hidden' };
  }

  const opacity = normalizeCssValue(style.opacity);
  if (opacity === '0' || opacity === '0.0') {
    return { field: `${fieldPrefix}.opacity`, value: '0' };
  }

  return null;
}

function hidingReasonFromClassName(
  className: string | undefined,
  fieldName = 'className'
): CssHidingReason | null {
  for (const token of classTokens(className)) {
    if (HIDING_CLASS_TOKENS.has(token)) {
      return { field: fieldName, value: token };
    }
  }
  return null;
}

function hidingReasonFromNode(node: LayoutNode): string | null {
  const styleReason =
    'style' in node && node.style ? hidingReasonFromStyle(node.style) : null;
  if (styleReason) return formatCssHidingReason(styleReason);

  const classNameReason =
    'className' in node ? hidingReasonFromClassName(node.className) : null;
  if (classNameReason) return formatCssHidingReason(classNameReason);

  if (node.type === 'OASISMultiselect') {
    const checklistStyleReason = node.checklistStyle
      ? hidingReasonFromStyle(node.checklistStyle, 'checklistStyle')
      : null;
    if (checklistStyleReason) {
      return formatCssHidingReason(checklistStyleReason);
    }

    const checklistClassReason = hidingReasonFromClassName(
      node.checklistClassName,
      'checklistClassName'
    );
    if (checklistClassReason) {
      return formatCssHidingReason(checklistClassReason);
    }
  }

  if (node.type === 'table') {
    const tableStyleReason = hidingReasonFromTableStyles(node);
    if (tableStyleReason) return formatCssHidingReason(tableStyleReason);
  }

  const extraClassNames = collectExtraClassNames(node);
  for (const className of extraClassNames) {
    const reason = hidingReasonFromClassName(className);
    if (reason) return formatCssHidingReason(reason);
  }

  return null;
}

function firstHidingStyleReason(
  slots: Array<[LayoutInlineStyle | undefined, string]>
): CssHidingReason | null {
  for (const [style, fieldName] of slots) {
    if (!style) continue;
    const reason = hidingReasonFromStyle(style, fieldName);
    if (reason) return reason;
  }
  return null;
}

/** Table-level inline styles — mirrors the `'table'` entry in `collectExtraClassNames`. */
function hidingReasonFromTableStyles(node: TableNode): CssHidingReason | null {
  return firstHidingStyleReason([
    [node.tableStyle, 'tableStyle'],
    [node.rowStyle, 'rowStyle'],
    [node.rowHeaderStyle, 'rowHeaderStyle'],
    [node.colHeaderStyle, 'colHeaderStyle'],
    [node.rowLabelStyle, 'rowLabelStyle'],
    [node.cellStyle, 'cellStyle'],
  ]);
}

/** Per-row `<tr>` / answer `<td>` styling merged in `Table.tsx` — not on the table node itself. */
function hidingReasonFromTableRow(
  row: TableRowEntry,
  rowIndex: number
): string | null {
  const prefix = `rows[${rowIndex}]`;
  const styleReason = firstHidingStyleReason([
    [row.rowStyle, `${prefix}.rowStyle`],
    [row.cellStyle, `${prefix}.cellStyle`],
  ]);
  if (styleReason) return formatCssHidingReason(styleReason);

  const checks: Array<[string | undefined, string]> = [
    [row.rowClassName, `${prefix}.rowClassName`],
    [row.cellClassName, `${prefix}.cellClassName`],
  ];
  for (const [className, fieldName] of checks) {
    const reason = hidingReasonFromClassName(className, fieldName);
    if (reason) return formatCssHidingReason(reason);
  }
  return null;
}

function collectExtraClassNames(node: LayoutNode): Array<string | undefined> {
  switch (node.type) {
    case 'OASISRadio':
      return [
        node.innerClassName,
        node.labelClassName,
        node.groupClassName,
        node.optionRowClassName,
        node.optionLabelClassName,
        node.controlClassName,
        node.itemClassName,
      ];
    case 'OASISYesNoCheckbox':
      return [
        node.innerClassName,
        node.labelClassName,
        node.optionRowClassName,
        node.controlClassName,
        node.checkboxClassName,
      ];
    case 'OASISSelect':
      return [
        node.innerClassName,
        node.labelClassName,
        node.controlClassName,
        node.triggerClassName,
      ];
    case 'OASISText':
    case 'OASISDate':
    case 'OASISNumber':
      return [
        node.innerClassName,
        node.labelClassName,
        node.controlClassName,
        node.inputClassName,
      ];
    case 'OASISTime':
      return [
        node.innerClassName,
        node.labelClassName,
        node.controlClassName,
        node.inputClassName,
      ];
    case 'OASISMultiselect':
      return [
        node.innerClassName,
        node.labelClassName,
        node.checklistClassName,
        node.optionRowClassName,
        node.optionLabelClassName,
        node.controlClassName,
        node.checkboxClassName,
      ];
    case 'OASISMultiselectOption':
      return [
        node.className,
        node.labelClassName,
        node.optionRowClassName,
        node.controlClassName,
        node.checkboxClassName,
      ];
    case 'radio-option':
      return [node.className, node.controlClassName, node.itemClassName];
    case 'table':
      return [
        node.className,
        node.tableClassName,
        node.rowClassName,
        node.rowHeaderClassName,
        node.colHeaderClassName,
        node.rowLabelClassName,
        node.cellClassName,
      ];
    case 'card':
    case 'stack':
    case 'grid':
    case 'flex':
    case 'box':
    case 'indent':
    case 'field-row':
    case 'divider':
    case 'static':
    case 'text':
    case 'display':
    case 'section-label':
    case 'sum':
    case 'conditional':
    case 'disabled-when':
    case 'repeat':
      return [];
    default:
      return assertLayoutNodeExhaustive(node);
  }
}

function mergeHiddenQid(
  byQid: Map<string, LayoutPlainCssHiddenQid>,
  qid: string,
  reason: string
) {
  if (byQid.has(qid)) return;
  byQid.set(qid, { qid, reason });
}

/**
 * Required schema QIDs that sit under plain CSS hiding on the field node or an ancestor wrapper.
 *
 * Checks inline `style`, Tailwind `className` tokens, multiselect checklist props, and
 * table/row style slots for obvious hide patterns (`display:none`, `visibility:hidden`,
 * `opacity:0`, `hidden`, etc.).
 */
export function collectLayoutPlainCssHiddenQids(
  nodes: LayoutNode[],
  schemaQids: Iterable<string>
): LayoutPlainCssHiddenQid[] {
  const schemaQidSet = new Set(schemaQids);
  const byQid = new Map<string, LayoutPlainCssHiddenQid>();

  const walk = (arr: LayoutNode[], inheritedHideReason: string | null) => {
    for (const node of arr) {
      const nodeReason = hidingReasonFromNode(node);
      const activeHideReason = nodeReason ?? inheritedHideReason;

      const recordQid = (qid: string) => {
        if (!schemaQidSet.has(qid) || activeHideReason == null) return;
        mergeHiddenQid(byQid, qid, activeHideReason);
      };

      switch (node.type) {
        case 'card':
        case 'stack':
        case 'grid':
        case 'flex':
        case 'box':
        case 'indent':
        case 'field-row':
        case 'divider':
        case 'static':
        case 'text':
        case 'display':
        case 'section-label':
        case 'sum':
        case 'table':
        case 'conditional':
        case 'disabled-when':
        case 'repeat':
        case 'OASISMultiselectOption':
          break;
        case 'OASISRadio':
        case 'OASISYesNoCheckbox':
        case 'OASISSelect':
        case 'OASISText':
        case 'OASISDate':
        case 'OASISTime':
        case 'OASISNumber':
        case 'OASISMultiselect':
        case 'radio-option':
          recordQid(node.qid);
          break;
        default:
          assertLayoutNodeExhaustive(node);
      }

      if (node.type === 'table') {
        for (let ri = 0; ri < node.rows.length; ri++) {
          const row = node.rows[ri]!;
          const rowReason = hidingReasonFromTableRow(row, ri);
          walk(row.cells, rowReason ?? activeHideReason);
        }
      } else {
        walkLayoutStructureChildren(node, childList =>
          walk(childList, activeHideReason)
        );
      }
    }
  };

  walk(nodes, null);
  return [...byQid.values()].sort((a, b) => a.qid.localeCompare(b.qid));
}
